import { Component, OnInit } from '@angular/core';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';
import * as XLSX from 'xlsx';
import { tervSorokbol } from './irattari-terv-import';

/**
 *  Irattár: irattárba helyezés, megőrzési idő, selejtezés, levéltári átadás,
 *  és maga az irattári terv (tételszám → megőrzési idő) karbantartása.
 */
@Component({
  selector: 'app-ikt-irattar',
  templateUrl: './irattar.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktIrattarComponent implements OnInit {
  nezet: 'ugyiratok' | 'kolcsonzes' | 'selejtezes' | 'terv' = 'ugyiratok';

  elintezett: any[] = [];
  irattarban: any[] = [];
  kolcsonzesek: any[] = [];
  jegyzokonyvek: any[] = [];
  terv: any[] = [];
  tervEv = new Date().getFullYear();
  /** Excelből betöltött, még el nem mentett terv – amíg igaz, figyelmeztetünk. */
  tervImportalt = false;
  betoltes = false;

  /** Tömeges selejtezéshez / levéltári átadáshoz kijelölt ügyiratok. */
  kijelolt = new Set<string>();
  jegyzokonyv: any = { tipus: 'selejtezes', jegyzokonyv_szam: '', leiras: '', leveltar: '', tervezett_datum: '' };
  /** 20/2012. EMMI r. 86. § (2): ennyi nappal a selejtezés előtt kell bejelenteni a levéltárnak. */
  bejelentesNap = 30;
  /** A folyamatban lévő eljárás épp szerkesztett lépése (bejelentés / végrehajtás). */
  szerk: any = null;

  /* Alapértelmezett irattári terv (20/2012. EMMI r. 1. melléklet) – előnézet, majd betöltés. */
  alapNyitva = false;
  alapAdat: any = null;
  alapKijelolt = new Set<string>();
  alapToltodik = false;
  ujKolcsonzes: any = { ugyirat: '', kolcsonzo: '', cel: '', visszahozando: '' };

  constructor(private ikt: IktatgatoService, private data: DataService) {}

  async ngOnInit() { await this.betolt(); }

  async betolt() {
    this.betoltes = true;
    try {
      this.elintezett = (await this.ikt.ugyiratok({ allapot: 'elintezve' })).ugyiratok || [];
      const it = await this.ikt.irattar();
      this.irattarban = (it.ugyiratok || []).filter((u: any) => u.allapot === 'irattarban');
      this.kolcsonzesek = it.kolcsonzesek || [];
      this.jegyzokonyvek = it.jegyzokonyvek || [];
      this.bejelentesNap = Number(it.selejtezes_bejelentes_nap) || 30;
      this.terv = await this.ikt.terv(this.tervEv);
      this.tervImportalt = false;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az irattárat.', 'error');
    } finally { this.betoltes = false; }
  }

  /* ── tömeges selejtezés / levéltári átadás ── */

  valt(u: any, be: boolean) { if (be) this.kijelolt.add(u.id); else this.kijelolt.delete(u.id); }
  kivan(u: any) { return this.kijelolt.has(u.id); }
  mindetSelejtezhetot() {
    for (const u of this.irattarban) if (this.selejtezheto(u) && !u.kolcsonzes_id) this.kijelolt.add(u.id);
  }

  /** Kijelölhető-e a kiválasztott műveletre (selejtezésnél a tiltott és az eljárásban lévő nem). */
  kijelolheto(u: any): boolean {
    if (u.kolcsonzes_id || u.eljarasban) return false;
    return this.jegyzokonyv.tipus !== 'selejtezes' || !u.selejtezes_tiltas;
  }

  /**
   *  Selejtezésnél csak a TERVEZETT eljárás jön létre (jegyzék), semmi nem semmisül meg:
   *  utána be kell jelenteni a levéltárnak, és csak a bejelentéstől számított 30. naptól
   *  hajtható végre (20/2012. EMMI r. 86. § (2)). A levéltári átadás egy lépésben megy.
   */
  async selejtez() {
    if (!this.kijelolt.size) { this.data.sendToast('Jelölj ki legalább egy ügyiratot.', 'warning'); return; }
    const selejtezes = this.jegyzokonyv.tipus === 'selejtezes';
    const kerdes = selejtezes
      ? `${this.kijelolt.size} ügyiratból selejtezési jegyzék (tervezett eljárás) készül. Ezzel még semmi nem selejteződik: `
        + `a levéltárnak be kell jelenteni, és csak a bejelentéstől számított ${this.bejelentesNap}. naptól hajtható végre. Folytatod?`
      : `${this.kijelolt.size} ügyirat levéltári átadása jegyzőkönyvvel. Folytatod?`;
    if (!confirm(kerdes)) return;
    try {
      const r = await this.ikt.selejtez({ ...this.jegyzokonyv, ugyiratok: Array.from(this.kijelolt) });
      const kimaradt = r.kihagyva?.length
        ? ` ${r.kihagyva.length} kimaradt: ${r.kihagyva.slice(0, 3).map((k: any) => `${k.iktatoszam} (${k.ok})`).join('; ')}` : '';
      this.data.sendToast(selejtezes
        ? `Selejtezési eljárás létrehozva (${r.tetel_db} tétel). Következő lépés: bejelentés a levéltárnak.${kimaradt}`
        : `${r.elintezve} ügyirat átadva.${kimaradt}`, r.kihagyva?.length ? 'warning' : 'success');
      this.kijelolt.clear();
      await this.betolt();
    } catch (e: any) {
      const k = e?.error?.kihagyva;
      this.data.sendToast((e?.error?.error || 'A művelet nem sikerült.')
        + (k?.length ? ' ' + k.slice(0, 3).map((x: any) => `${x.iktatoszam}: ${x.ok}`).join('; ') : ''), 'error');
    }
  }

  /* ── selejtezési eljárás lépései ── */

  ma(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  eljarasAllapot(j: any): string {
    if (j.tipus !== 'selejtezes') return 'levéltári átadás';
    return ({ tervezett: 'tervezett – bejelentésre vár', bejelentve: 'levéltárnak bejelentve',
      vegrehajtva: 'végrehajtva', visszavonva: 'visszavonva' } as any)[j.allapot] || j.allapot;
  }
  eljarasSzin(j: any): string {
    return ({ tervezett: 'warning', bejelentve: 'tertiary', vegrehajtva: 'medium', visszavonva: 'medium' } as any)[j.allapot] || 'medium';
  }

  bejelentesNyit(j: any) {
    this.szerk = { id: j.id, mod: 'bejelentes', bejelentve: this.ma(), leveltar: j.leveltar || '',
      bejelentes_iktatoszam: '', tervezett_datum: j.tervezett_datum || '' };
  }
  vegrehajtasNyit(j: any) {
    this.szerk = { id: j.id, mod: 'vegrehajtas', datum: this.ma(), elrendelte: j.elrendelte || '',
      jegyzokonyv_szam: j.jegyzokonyv_szam || '', ettol: j.vegrehajthato_ettol, tetel_db: j.tetel_db };
  }

  async szerkMent() {
    const s = this.szerk;
    if (!s) return;
    try {
      if (s.mod === 'bejelentes') {
        const r = await this.ikt.selejtezesBejelent(s.id, s);
        this.data.sendToast(`Levéltárnak bejelentve. A selejtezés ${r.vegrehajthato_ettol}-tól hajtható végre.`);
      } else {
        if (!confirm(`A jegyzékben szereplő ügyiratok selejtezése (${s.tetel_db} tétel) – a művelet nem vonható vissza. Folytatod?`)) return;
        const r = await this.ikt.selejtezesVegrehajt(s.id, s);
        this.data.sendToast(`${r.elintezve} ügyirat selejtezve.`
          + (r.kihagyva?.length ? ` ${r.kihagyva.length} kikerült az eljárásból (időközben nem selejtezhető).` : ''),
          r.kihagyva?.length ? 'warning' : 'success');
      }
      this.szerk = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A művelet nem sikerült.', 'error');
    }
  }

  async visszavon(j: any) {
    const indok = window.prompt('A selejtezési eljárás visszavonásának indoka:') || '';
    if (!indok.trim()) return;
    try {
      await this.ikt.selejtezesVisszavon(j.id, indok.trim());
      this.data.sendToast('Az eljárás visszavonva, a tételek az irattárban maradnak.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    }
  }

  /** A jegyzék nyomtatható változata (a levéltári bejelentéshez és a jegyzőkönyvhöz). */
  async jegyzekNyomtat(j: any) {
    try {
      const r = await this.ikt.jegyzokonyv(j.id);
      const e = (v: any) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
      const f = r.jegyzokonyv || {};
      const sorok = (r.tetelek || []).map((t: any, i: number) => `<tr><td>${i + 1}.</td><td>${e(t.iktatoszam)}</td><td>${e(t.targy)}</td>`
        + `<td>${e(t.tetelszam)} ${e(t.tetel_megnevezes)}</td><td>${e(t.irattarba)}</td><td>${e(t.megorzes_ev ? t.megorzes_ev + ' év' : '')}</td></tr>`).join('');
      const cim = f.tipus === 'selejtezes' ? 'Selejtezési jegyzék' : 'Levéltári átadás jegyzéke';
      const w = window.open('', '_blank');
      if (!w) { this.data.sendToast('A böngésző letiltotta az új ablakot.', 'warning'); return; }
      w.document.write(`<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>${cim}</title>
        <style>body{font-family:Arial,sans-serif;font-size:11pt;margin:18mm}table{border-collapse:collapse;width:100%}
        th,td{border:1px solid #444;padding:3px 6px;text-align:left;vertical-align:top}th{background:#eee}</style></head><body>
        <h2>${cim}</h2>
        <p>Jegyzőkönyv száma: ${e(f.jegyzokonyv_szam || '—')}<br>Állapot: ${e(this.eljarasAllapot(f))}<br>
        ${f.tervezett_datum ? 'Tervezett időpont: ' + e(f.tervezett_datum) + '<br>' : ''}
        ${f.bejelentve ? 'Levéltárnak bejelentve: ' + e(f.bejelentve) + ' (' + e(f.leveltar) + ')' + (f.bejelentes_iktatoszam ? ', iktatószám: ' + e(f.bejelentes_iktatoszam) : '') + '<br>' : ''}
        ${f.vegrehajtva ? 'Végrehajtva: ' + e(f.vegrehajtva) + ', elrendelte: ' + e(f.elrendelte) + '<br>' : ''}
        ${f.leiras ? 'Indoklás: ' + e(f.leiras) : ''}</p>
        <table><thead><tr><th></th><th>Iktatószám</th><th>Tárgy</th><th>Irattári tétel</th><th>Irattárba</th><th>Megőrzés</th></tr></thead>
        <tbody>${sorok}</tbody></table>
        <p style="margin-top:28mm">………………………………………<br>igazgató</p></body></html>`);
      w.document.close();
      w.focus();
      w.print();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A jegyzék nem tölthető be.', 'error');
    }
  }

  /* ── alapértelmezett irattári terv ── */

  async alapElonezet() {
    this.alapNyitva = true;
    this.alapToltodik = true;
    this.alapAdat = null;
    try {
      this.alapAdat = await this.ikt.tervAlap(this.tervEv);
      this.alapKijelolt = new Set((this.alapAdat.tetelek || []).filter((t: any) => t.allapot === 'uj').map((t: any) => t.kulcs));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az alapértelmezett terv nem tölthető be.', 'error');
      this.alapNyitva = false;
    } finally { this.alapToltodik = false; }
  }
  alapValt(t: any, be: boolean) { if (be) this.alapKijelolt.add(t.kulcs); else this.alapKijelolt.delete(t.kulcs); }
  alapAllapotSzoveg(t: any): string {
    return ({ uj: 'felvehető', megvan: 'már szerepel', hasonlo: 'hasonló tétel már szerepel', utkozik: 'a tételszám foglalt' } as any)[t.allapot] || t.allapot;
  }
  async alapBetolt() {
    if (!this.alapKijelolt.size) { this.data.sendToast('Nincs kijelölt felvehető tétel.', 'warning'); return; }
    if (this.tervImportalt && !confirm('Az Excelből betöltött, még nem mentett tételek elvesznek. Folytatod?')) return;
    if (!confirm(`${this.alapKijelolt.size} hiányzó tétel felvétele a ${this.tervEv}. évi irattári tervbe. A meglévő tételek nem változnak. Folytatod?`)) return;
    try {
      const r = await this.ikt.tervAlapBetolt(this.tervEv, Array.from(this.alapKijelolt));
      this.data.sendToast(`${r.felveve} tétel felvéve az irattári tervbe.`);
      this.alapNyitva = false;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A betöltés nem sikerült.', 'error');
    }
  }

  /* ── kölcsönzés ── */

  async kolcsonoz(u: any) {
    const ki = window.prompt(`Ki viszi el a(z) ${u.iktatoszam} ügyiratot?`) || '';
    if (!ki.trim()) return;
    try {
      await this.ikt.kolcsonoz({ ugyirat: u.id, kolcsonzo: ki.trim() });
      this.data.sendToast('Kikölcsönözve.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kölcsönzés nem sikerült.', 'error');
    }
  }

  async visszavesz(k: any) {
    try {
      await this.ikt.kolcsonzesVissza(k.id);
      this.data.sendToast('Visszavéve.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavétel nem sikerült.', 'error');
    }
  }

  /** Selejtezhető, ha az irattárba helyezés éve + megőrzési idő letelt, és semmi nem tiltja. */
  selejtezheto(u: any): boolean {
    return !!u.selejtezheto_ev && Number(u.selejtezheto_ev) <= new Date().getFullYear()
      && !u.selejtezes_tiltas && !u.eljarasban;
  }

  async allapot(u: any, allapot: string, kerdes: string) {
    if (!confirm(kerdes)) return;
    try {
      await this.ikt.ugyiratModosit(u.id, { allapot });
      this.data.sendToast('Kész.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A művelet nem sikerült.', 'error');
    }
  }

  ujTetel() { this.terv.push({ tetelszam: '', megnevezes: '', csoport: null, megorzes_ev: 5, nem_selejtezheto: 0, leveltari: 0 }); }

  /**
   *  IRATTÁRI TERV IMPORTÁLÁSA EXCELBŐL (pl. a KRÉTA „Exp_IrattariTervTetelListModel”
   *  exportja). A tételek a szerkesztőbe kerülnek, NEM mentődnek azonnal: átnézhetők,
   *  javíthatók, és a „Terv mentése” rögzíti őket – a terv mentése a kiválasztott év
   *  teljes tervét cseréli, ezért ha már van tétel, előtte rákérdezünk.
   */
  async tervImport(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const fajl = input.files?.[0];
    input.value = '';
    if (!fajl) return;
    try {
      const wb = XLSX.read(await fajl.arrayBuffer(), { type: 'array' });
      const lap = wb.Sheets[wb.SheetNames[0]];
      const sorok = XLSX.utils.sheet_to_json<unknown[]>(lap, { header: 1, raw: false, defval: null });
      const r = tervSorokbol(sorok);
      if (r.hiba || !r.tetelek.length) {
        this.data.sendToast(r.hiba || 'A fájlban nincs importálható tétel.', 'error');
        return;
      }
      if (this.terv.length && !confirm(
        `A(z) ${fajl.name} ${r.tetelek.length} tétele lecseréli a ${this.tervEv}. évi terv most látható `
        + `${this.terv.length} tételét. A változás csak a „Terv mentése” gombra kerül rögzítésre. Folytatod?`)) return;
      this.terv = r.tetelek;
      this.tervImportalt = true;
      this.data.sendToast(`${r.tetelek.length} tétel betöltve`
        + (r.kihagyott.length ? `, ${r.kihagyott.length} sor kimaradt (${r.kihagyott.slice(0, 3).join('; ')})` : '')
        + '. Nézd át, majd mentsd a tervet.', r.kihagyott.length ? 'warning' : 'success');
    } catch (e: any) {
      this.data.sendToast('A fájl nem olvasható be: ' + (e?.message || e), 'error');
    }
  }
  torolTetel(i: number) { this.terv.splice(i, 1); }

  async tervMent() {
    try {
      const r = await this.ikt.tervMent(this.tervEv, this.terv);
      this.data.sendToast(`Irattári terv mentve (${r.tetelek} tétel).`
        + (r.kenyszeritve?.length ? ` A rendelet szerint nem selejtezhető tétel(ek) visszaállítva: ${r.kenyszeritve.join(', ')}.` : ''),
        r.kenyszeritve?.length ? 'warning' : 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }
}
